/**
 * TileMap.ts
 * Tile-based map rendering for world and dungeons
 */

import { Renderer } from './Renderer';
import { SpriteManager } from './SpriteManager';

// Legacy type support
interface Viewport {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TileMapData {
  width: number;
  height: number;
  tiles?: number[][];
  layers?: {
    ground: number[][];
    objects?: number[][];
    overlay?: number[][];
  };
}

export class TileMap {
  private tiles: number[][];
  private objectLayer: number[][] | null;
  private overlayLayer: number[][] | null;
  tileSize: number;
  viewport: Viewport;
  width: number;
  height: number;
  private spriteManager: SpriteManager;
  private tilesetName: string;
  private walkableTiles: Set<number>;

  constructor(spriteManager: SpriteManager, tilesetName: string, tileSize: number = 16) {
    this.spriteManager = spriteManager;
    this.tilesetName = tilesetName;
    this.tileSize = tileSize;
    this.tiles = [];
    this.objectLayer = null;
    this.overlayLayer = null;
    this.width = 0;
    this.height = 0;
    this.viewport = { x: 0, y: 0, width: 256, height: 240 };

    // Default walkable tiles (0 = empty, 1-10 = walkable ground)
    this.walkableTiles = new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
  }

  /**
   * Set the map data
   */
  setMap(data: TileMapData): void {
    if (data.layers) {
      this.tiles = data.layers.ground;
      this.objectLayer = data.layers.objects || null;
      this.overlayLayer = data.layers.overlay || null;
    } else {
      this.tiles = data.tiles || [];
    }

    this.width = data.width;
    this.height = data.height;
  }

  /**
   * Set walkable tile IDs
   */
  setWalkableTiles(tileIds: number[]): void {
    this.walkableTiles = new Set(tileIds);
  }

  /**
   * Render the visible portion of the map
   */
  render(renderer: Renderer): void {
    const startCol = Math.floor(this.viewport.x / this.tileSize);
    const endCol = Math.ceil((this.viewport.x + this.viewport.width) / this.tileSize);
    const startRow = Math.floor(this.viewport.y / this.tileSize);
    const endRow = Math.ceil((this.viewport.y + this.viewport.height) / this.tileSize);

    // Clamp to map bounds
    const clampedStartCol = Math.max(0, startCol);
    const clampedEndCol = Math.min(this.width, endCol);
    const clampedStartRow = Math.max(0, startRow);
    const clampedEndRow = Math.min(this.height, endRow);

    // Render ground layer
    this.renderLayer(renderer, this.tiles, clampedStartRow, clampedEndRow, clampedStartCol, clampedEndCol);

    // Render object layer
    if (this.objectLayer) {
      this.renderLayer(renderer, this.objectLayer, clampedStartRow, clampedEndRow, clampedStartCol, clampedEndCol);
    }

    // Render overlay layer
    if (this.overlayLayer) {
      this.renderLayer(renderer, this.overlayLayer, clampedStartRow, clampedEndRow, clampedStartCol, clampedEndCol);
    }
  }

  /**
   * Render a single layer
   */
  private renderLayer(
    renderer: Renderer,
    layer: number[][],
    startRow: number,
    endRow: number,
    startCol: number,
    endCol: number
  ): void {
    for (let row = startRow; row < endRow; row++) {
      if (!layer[row]) continue;

      for (let col = startCol; col < endCol; col++) {
        const tileId = layer[row]?.[col];
        if (tileId === 0 || tileId === undefined) continue; // Skip empty tiles

        const sprite = this.spriteManager.getSprite(this.tilesetName, tileId);
        if (!sprite) continue;

        const screenX = col * this.tileSize - this.viewport.x;
        const screenY = row * this.tileSize - this.viewport.y;

        renderer.drawSprite(sprite, screenX, screenY);
      }
    }
  }

  /**
   * Get tile ID at position
   */
  getTile(x: number, y: number, layer: 'ground' | 'object' | 'overlay' = 'ground'): number {
    const col = Math.floor(x / this.tileSize);
    const row = Math.floor(y / this.tileSize);

    if (row < 0 || row >= this.height || col < 0 || col >= this.width) {
      return -1; // Out of bounds
    }

    let targetLayer: number[][];
    switch (layer) {
      case 'object':
        targetLayer = this.objectLayer || this.tiles;
        break;
      case 'overlay':
        targetLayer = this.overlayLayer || this.tiles;
        break;
      default:
        targetLayer = this.tiles;
    }

    return targetLayer[row]?.[col] ?? 0;
  }

  /**
   * Check if a position is walkable
   */
  isWalkable(x: number, y: number): boolean {
    const groundTile = this.getTile(x, y, 'ground');
    const objectTile = this.getTile(x, y, 'object');

    // If there's an object tile, it takes priority
    if (objectTile > 0) {
      return this.walkableTiles.has(objectTile);
    }

    return this.walkableTiles.has(groundTile);
  }

  /**
   * Center viewport on position
   */
  centerOn(x: number, y: number): void {
    this.viewport.x = x - this.viewport.width / 2;
    this.viewport.y = y - this.viewport.height / 2;
    this.clampViewport();
  }

  /**
   * Move viewport
   */
  moveViewport(dx: number, dy: number): void {
    this.viewport.x += dx;
    this.viewport.y += dy;
    this.clampViewport();
  }

  /**
   * Clamp viewport to map bounds
   */
  private clampViewport(): void {
    const mapPixelWidth = this.width * this.tileSize;
    const mapPixelHeight = this.height * this.tileSize;

    this.viewport.x = Math.max(0, Math.min(this.viewport.x, mapPixelWidth - this.viewport.width));
    this.viewport.y = Math.max(0, Math.min(this.viewport.y, mapPixelHeight - this.viewport.height));
  }

  /**
   * Convert screen coordinates to world coordinates
   */
  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: screenX + this.viewport.x,
      y: screenY + this.viewport.y
    };
  }

  /**
   * Convert world coordinates to screen coordinates
   */
  worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    return {
      x: worldX - this.viewport.x,
      y: worldY - this.viewport.y
    };
  }

  /**
   * Get tile coordinates from world position
   */
  getTileCoordinates(worldX: number, worldY: number): { col: number; row: number } {
    return {
      col: Math.floor(worldX / this.tileSize),
      row: Math.floor(worldY / this.tileSize)
    };
  }
}
